import { memo } from 'react';
import { Html } from '@react-three/drei';
import type { TacticsCoachingPoint } from '@4cm/core/tactics';

/**
 * Coaching-point pin — a small disc + text card pinned to a pitch
 * position. Used by both the 2D and 3D tactics boards.
 *
 * SURFACES
 * --------
 * The pin renders different inner content depending on the surface,
 * but the public component is the same. Pass `surface='svg'` when
 * mounting inside the 2D SVG (the card lives in foreignObject so the
 * coach gets real HTML for text) and `surface='r3f'` when mounting in
 * a React Three Fiber scene (we use drei's <Html> so the card always
 * faces the camera regardless of orbit angle).
 *
 * VISIBILITY
 * ----------
 * If `point.keyframeId` is set, the pin renders only when
 * `activeKeyframeId === point.keyframeId`. When `point.keyframeId` is
 * undefined, the pin is always visible — these are the "general"
 * coaching points the coach can mention at any time.
 *
 * The component is presentational only — it neither commits nor
 * mutates the layout. Editing flows live in the upcoming ZoneEditor
 * (out of scope for this agent).
 */
export interface CoachingPointPinProps {
  point: TacticsCoachingPoint;
  /** The keyframe currently playing back, if any. */
  activeKeyframeId?: string;
  /** Where this pin is being rendered — drives the geometry primitives. */
  surface: 'svg' | 'r3f';
}

// Card sizing — chosen to read on a phone screen without crowding the
// pitch. The card is 18 metres wide in world space (about the width of
// the penalty box) which scales naturally on both surfaces.
const CARD_WIDTH_M = 18;
const CARD_HEIGHT_M = 6;
const DISC_RADIUS_M = 0.9;
const HOTSPOT_FILL = '#fde68a';
const HOTSPOT_STROKE = '#f59e0b';
const CARD_BG = 'rgba(8, 28, 18, 0.92)';
const CARD_TEXT = '#f5f5f5';

function shouldRender(point: TacticsCoachingPoint, activeKeyframeId: string | undefined): boolean {
  if (!point.keyframeId) return true;
  return point.keyframeId === activeKeyframeId;
}

/**
 * Inline card content shared across surfaces. Avoids re-implementing
 * the text styling twice and keeps the visual identity consistent.
 */
function CardContent({ text }: { text: string }) {
  return (
    <div
      style={{
        background: CARD_BG,
        color: CARD_TEXT,
        padding: '6px 10px',
        borderRadius: 6,
        border: '1px solid rgba(255,255,255,0.25)',
        fontSize: 13,
        lineHeight: 1.3,
        maxWidth: 200,
        boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {text}
    </div>
  );
}

function CoachingPointPinInner({ point, activeKeyframeId, surface }: CoachingPointPinProps) {
  if (!shouldRender(point, activeKeyframeId)) return null;

  if (surface === 'svg') {
    // 2D pitch is LANDSCAPE: SVG-x = -world.z, SVG-y = world.x.
    const cx = -point.at.z;
    const cy = point.at.x;
    return (
      <g
        className="t2d-cp-pin"
        data-testid={`t2d-cp-${point.id}`}
        data-keyframe-id={point.keyframeId ?? ''}
        pointerEvents="none"
      >
        {/* Hotspot disc — small marker pinned at the world position. */}
        <circle
          cx={cx}
          cy={cy}
          r={DISC_RADIUS_M}
          fill={HOTSPOT_FILL}
          stroke={HOTSPOT_STROKE}
          strokeWidth={0.2}
        />
        {/* Text card — anchored above-right of the disc so it doesn't
            cover the tokens directly below it. foreignObject lets us
            render real HTML inside the SVG so the card auto-flows + the
            text rotates correctly regardless of any future pitch rotation
            via CSS transforms. */}
        <foreignObject
          x={cx + DISC_RADIUS_M + 0.6}
          y={cy - CARD_HEIGHT_M - 0.4}
          width={CARD_WIDTH_M}
          height={CARD_HEIGHT_M}
          style={{ overflow: 'visible' }}
        >
          <CardContent text={point.text} />
        </foreignObject>
      </g>
    );
  }

  // r3f surface — render via drei's <Html>, which projects to screen
  // space and stays camera-facing regardless of orbit angle. The disc
  // is a small mesh on the field; the card hovers above it.
  return (
    <group
      position={[point.at.x, 0, point.at.z]}
      name={`coaching-point-${point.id}`}
    >
      {/* Hotspot disc — flat circle on the pitch slightly above the
          zone layer so it reads above zone fills. */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0.01, 0]}
        raycast={() => undefined}
      >
        <circleGeometry args={[DISC_RADIUS_M, 24]} />
        <meshStandardMaterial
          color={HOTSPOT_FILL}
          emissive={HOTSPOT_STROKE}
          emissiveIntensity={0.2}
          roughness={0.7}
        />
      </mesh>
      <Html
        position={[0, 1.5, 0]}
        center
        distanceFactor={20}
        data-testid={`t3d-cp-${point.id}`}
        data-keyframe-id={point.keyframeId ?? ''}
        // Make the card non-interactive — coaching points are
        // teaching annotations, not click targets.
        style={{ pointerEvents: 'none' }}
      >
        <CardContent text={point.text} />
      </Html>
    </group>
  );
}

/** Memoised — the active keyframe changes during playback ~60 times a
 *  second on the 3D board; without memo each pin would re-render even
 *  when its visibility is stable. */
export const CoachingPointPin = memo(CoachingPointPinInner);
